import { styled } from "@pigment-css/react";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { Checkbox } from "@acto/taskpane/core/components/checkbox/Checkbox";
import { MarkdownRenderer } from "@acto/taskpane/core/components/markdown/MarkdownRenderer";
import { useCustomerTodosByClientId } from "@acto/taskpane/core/data-access/customers/useClientWithTodos";
import { useNotes } from "@acto/taskpane/core/data-access/notes";
import { useNoteTodos } from "@acto/taskpane/core/data-access/todos/useNoteTodos";
import { TodoStatus } from "@acto/gql/graphql";
import { Note } from "@acto/taskpane/core/domain";
import { MarkdownBody } from "../ui";

interface TodoSidePanelProps {
  clientId: string;
  note?: Note; // Optional - when provided, displays this specific note instead of fetching
  noteId?: string | null; // Optional - when provided, fetches and displays todos for this specific note
  onTodoToggle: (todoId: string, isCompleted: boolean) => void;
  onCustomerClick?: (clientId: string, clientName: string) => void;
}

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[32],
  height: "100%",
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
}));

const Title = styled("h2")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[21],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
  "&[data-clickable='true']": {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const Subtitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const Section = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

const SummarySection = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[16],
}));

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[100],
  margin: 0,
  lineHeight: theme.font.lineHeight[30],
}));

const SummaryWrapper = styled(MarkdownBody)({
  "& > *:last-child": {
    marginBottom: 0,
  },
});

const TodoList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[16],
}));

const TodoItemContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.managementView.spacing[16],
}));

const CheckboxWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  flexShrink: 0,

  "& [role='checkbox'][data-state='unchecked']": {
    borderColor: theme.managementView.colors.neutral[56],
  },
}));

const TodoDetails = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[6],
  flex: 1,
  minWidth: 0,
}));

const TodoTitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.font.lineHeight[20],
  color: theme.managementView.colors.neutral[90],
  margin: 0,
  wordBreak: "break-word",
}));

const TodoDescription = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.managementView.font.lineHeight[164],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
  wordBreak: "break-word",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

export const TodoSidePanel = ({
  clientId,
  note: providedNote,
  noteId: providedNoteId,
  onTodoToggle,
  onCustomerClick,
}: TodoSidePanelProps) => {
  const t = useTranslations();
  const { locale } = useLocale();

  // Only fetch notes if not provided
  const { data: fetchedNotes } = useNotes({ clientId, limit: 1 });

  // Determine which note to use: provided note object > latest fetched note > null
  const note = providedNote ?? fetchedNotes?.[0];

  // Determine which noteId to use for fetching todos:
  // 1. Provided noteId prop (from completed appointments)
  // 2. Note object's id (if note was provided or fetched)
  const effectiveNoteId = providedNoteId ?? note?.id ?? "";

  // If we have a noteId, fetch todos for that specific note
  // Otherwise, fetch all todos for the customer (backward compatibility)
  // Use a large limit to fetch all todos (typically a note won't have more than 100 todos)
  const { data: noteTodos } = useNoteTodos({
    noteId: effectiveNoteId,
    processedStatuses: [TodoStatus.Open, TodoStatus.Completed],
    limit: 100,
    enabled: !!effectiveNoteId,
  });

  const { data: allCustomerTodos = [], clientName, clientExternalReference } = useCustomerTodosByClientId(clientId);

  // Use note-specific todos if we have a noteId, otherwise use all customer todos
  const todos = effectiveNoteId ? (noteTodos ?? []) : allCustomerTodos;

  const customerName = clientName ?? "";
  const date = note?.createdAt?.locale(locale).format("D MMMM YYYY") ?? "";
  const summary = note?.summary || note?.content || "";
  const hasSummary = summary.trim().length > 0;
  const hasTodos = todos.length > 0;

  return (
    <Container>
      <Header>
        <Title
          data-clickable={onCustomerClick ? "true" : undefined}
          onClick={onCustomerClick ? () => onCustomerClick(clientId, customerName) : undefined}
        >
          {customerName}
        </Title>
        <Subtitle>
          {clientExternalReference && `#${clientExternalReference}`}
          {clientExternalReference && date && " · "}
          {date}
        </Subtitle>
      </Header>

      {hasSummary && (
        <SummarySection>
          <SectionTitle>{t("managementView.todoSidePanel.summary")}</SectionTitle>
          <SummaryWrapper>
            <MarkdownRenderer content={summary} />
          </SummaryWrapper>
        </SummarySection>
      )}

      {hasTodos && (
        <Section>
          <SectionTitle>{t("managementView.todoSidePanel.todos")}</SectionTitle>
          <TodoList>
            {todos.map((todo) => {
              const isCompleted = !todo.isOpen();
              const displayTitle = todo.title || todo.description || "";
              return (
                <TodoItemContainer key={todo.id}>
                  <CheckboxWrapper
                    onClick={() => !isCompleted && onTodoToggle(todo.id, !isCompleted)}
                    style={{ cursor: isCompleted ? "not-allowed" : "pointer" }}
                  >
                    <Checkbox checked={isCompleted} disabled={isCompleted} />
                  </CheckboxWrapper>
                  <TodoDetails>
                    <TodoTitle>{displayTitle}</TodoTitle>
                    {todo.description && todo.title && <TodoDescription>{todo.description}</TodoDescription>}
                  </TodoDetails>
                </TodoItemContainer>
              );
            })}
          </TodoList>
        </Section>
      )}
    </Container>
  );
};
